import { getPlatformInfo } from 'blocks/DestinationPlatforms/utils';
import { DEFAULT_LANGUAGE } from 'constants/languages';
import { DEFAULT_PROJECT_NAME } from 'utils/constants';
import {
  DEFAULT_ASPECT_RATIO,
  VIDEO_CLIP_DEFAULT_DURATION_MILLIS,
  VIDEO_CLIP_MAX_DURATION_MILLIS,
} from '../constants';
import { VideoFlowStep } from '../types';
import { VideoFlowAction, VideoFlowState } from './types';

export const DEFAULT_STATE: VideoFlowState = {
  aspectRatioName: DEFAULT_ASPECT_RATIO,
  clipEndMillis: undefined,
  clipStartMillis: 0,
  editorState: undefined,
  isAspectRatioLocked: false,
  isAutoFrame: false,
  isObjectiveStepComplete: false,
  isFullEpisode: false,
  language: DEFAULT_LANGUAGE,
  mediaImportedArgs: undefined,
  objective: undefined,
  platform: undefined,
  projectName: DEFAULT_PROJECT_NAME,
  suggestedClipDurationMillis: 0,
  transcribe: true,
  useYoutubeMostReplayed: false,
  videoDurationMillis: undefined,
  videoSrc: undefined,
  videoSrcUrl: undefined,
  videoType: undefined,
};

export default function reducer(
  state: VideoFlowState = DEFAULT_STATE,
  action: VideoFlowAction,
): VideoFlowState {
  switch (action.type) {
    case 'step-change': {
      const { toStep, steps } = action.payload;

      // `objective` is stable so that the wizard UI is stable and shows something
      // even when the user hasn't selected an objective.  returning to the objective
      // step clears the user's selection by resetting the completed flag
      if (toStep.stepId === VideoFlowStep.OBJECTIVE) {
        return { ...state, isObjectiveStepComplete: false };
      }

      const uploadStepIndex = steps.findIndex(
        step => step.stepId === VideoFlowStep.UPLOAD_VIDEO,
      );

      // when the user goes back to the upload step or before it, the upload state
      // is reset
      if (uploadStepIndex >= 0 && uploadStepIndex >= toStep.index) {
        return {
          ...state,
          language: DEFAULT_STATE.language,
          platform: DEFAULT_STATE.platform,
          mediaImportedArgs: DEFAULT_STATE.mediaImportedArgs,
          projectName: DEFAULT_STATE.projectName,
          transcribe: DEFAULT_STATE.transcribe,
          useYoutubeMostReplayed: DEFAULT_STATE.useYoutubeMostReplayed,
          videoSrc: DEFAULT_STATE.videoSrc,
          videoSrcUrl: DEFAULT_STATE.videoSrcUrl,
          videoType: DEFAULT_STATE.videoType,
        };
      }

      return state;
    }

    case 'objective-select': {
      const { objective } = action.payload;

      return {
        ...state,
        isObjectiveStepComplete: true,
        objective,
      };
    }

    case 'submit': {
      const { exportState } = action.payload;
      return { ...state, editorState: exportState };
    }

    case 'aspect-ratio-select': {
      if (state.isAspectRatioLocked) return state;
      const { aspectRatioName } = action.payload;
      return { ...state, aspectRatioName };
    }

    case 'sample-video-select': {
      const { src } = action.payload;
      const hasSourceChanged = src !== state.videoSrcUrl;

      return {
        ...state,
        transcribe: hasSourceChanged ? false : state.transcribe,
        videoSrcUrl: src,
      };
    }

    case 'file-accepted': {
      const { file, srcUrl } = action.payload;
      const hasSourceChanged = srcUrl !== state.videoSrcUrl;

      return {
        ...state,
        videoSrc: file,
        videoSrcUrl: srcUrl,
        projectName: file.name,
        transcribe: hasSourceChanged ? false : state.transcribe,
      };
    }

    case 'media-accepted': {
      const { mediaImportedArgs } = action.payload;

      if (!mediaImportedArgs) {
        return state;
      }

      const { previewUrl, provider, title } = mediaImportedArgs;
      const hasSourceChanged = previewUrl !== state.videoSrcUrl;

      const patch: Partial<VideoFlowState> = {
        mediaImportedArgs,
        projectName: title,
        transcribe: hasSourceChanged ? false : state.transcribe,
        useYoutubeMostReplayed: false,
        videoSrcUrl: previewUrl,
      };

      if (
        state.objective === 'YouTubeMostReplayed' &&
        provider === 'youtube' &&
        mediaImportedArgs.hasMostReplayedData
      ) {
        patch.platform = 'youtube';
        patch.useYoutubeMostReplayed = true;
        patch.videoType = { id: 'short' };
      }

      return { ...state, ...patch };
    }

    case 'video-duration-change': {
      const { durationMillis } = action.payload;
      return {
        ...state,
        videoDurationMillis: durationMillis,
        clipStartMillis: 0,
        clipEndMillis: state.isFullEpisode
          ? durationMillis
          : Math.min(
              durationMillis,
              VIDEO_CLIP_DEFAULT_DURATION_MILLIS,
              VIDEO_CLIP_MAX_DURATION_MILLIS,
            ),
      };
    }

    case 'clip-millis-change': {
      const { startMillis, endMillis } = action.payload;

      return {
        ...state,
        clipStartMillis: startMillis,
        clipEndMillis: endMillis,
      };
    }

    case 'clip-selection-mode-change': {
      const { mode } = action.payload;
      return {
        ...state,
        clipStartMillis: 0,
        clipEndMillis:
          mode === 'full-episode'
            ? state.videoDurationMillis
            : Math.min(
                state.videoDurationMillis,
                VIDEO_CLIP_DEFAULT_DURATION_MILLIS,
                VIDEO_CLIP_MAX_DURATION_MILLIS,
              ),
        isFullEpisode: mode === 'full-episode',
      };
    }

    case 'clip-step-submit': {
      return {
        ...state,
        transcribe: true,
      };
    }

    case 'destination-select': {
      const { platform, videoType } = action.payload;
      const platformInfo = getPlatformInfo(platform, videoType?.id);

      return {
        ...state,
        aspectRatioName: state.isAspectRatioLocked
          ? state.aspectRatioName
          : platformInfo?.aspectRatio?.videoTranscript ?? state.aspectRatioName,
        platform,
        suggestedClipDurationMillis: platformInfo?.durationMs?.videoTranscript,
        transcribe: true,
        useYoutubeMostReplayed: false,
        videoType,
      };
    }

    case 'language-select': {
      const { language } = action.payload;
      return { ...state, language };
    }

    case 'transcription-change': {
      const { language, transcribe } = action.payload;
      return { ...state, language, transcribe };
    }

    case 'auto-frame-change': {
      const { isAutoFrame } = action.payload;
      return { ...state, isAutoFrame };
    }

    default:
      return state;
  }
}
